import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:5001/fc-court-booking-api/us-central1/api';

// Create axios instance with default config
const adminApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
adminApi.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    console.error('Admin API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
adminApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('Admin API Response Error:', error);
    if (error.response) {
      console.error('Error data:', error.response.data);
      console.error('Error status:', error.response.status);
    }
    return Promise.reject(error);
  }
);

// Admin API functions
export const adminApiService = {
  /**
   * Get all court types
   * @param {string} idToken - LINE ID token for authentication
   * @returns {Promise<Array>} Array of court types
   */
  getCourtTypes: async (idToken) => {
    try {
      const response = await adminApi.get(`/admin/court-types?idToken=${idToken}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to fetch court types');
    }
  },

  /**
   * Create new court type
   * @param {string} idToken - LINE ID token for authentication
   * @param {Object} courtTypeData - Court type data
   * @returns {Promise<Object>} Created court type
   */
  createCourtType: async (idToken, courtTypeData) => {
    try {
      const response = await adminApi.post(`/admin/court-types?idToken=${idToken}`, courtTypeData);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to create court type');
    }
  },

  /**
   * Update court type
   * @param {string} idToken - LINE ID token for authentication
   * @param {string} id - Court type ID
   * @param {Object} courtTypeData - Updated court type data
   * @returns {Promise<Object>} Updated court type
   */
  updateCourtType: async (idToken, id, courtTypeData) => {
    try {
      const response = await adminApi.put(`/admin/court-types/${id}?idToken=${idToken}`, courtTypeData);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to update court type');
    }
  },

  /**
   * Delete court type
   * @param {string} idToken - LINE ID token for authentication
   * @param {string} id - Court type ID
   * @returns {Promise<Object>} Deletion result
   */
  deleteCourtType: async (idToken, id) => {
    try {
      const response = await adminApi.delete(`/admin/court-types/${id}?idToken=${idToken}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to delete court type');
    }
  },

  /**
   * Multi-court batch booking (F4). bookings = [{courtId,date,startTime,endTime,duration}].
   * Returns { createdCount, skipped }. Conflicts are skipped, not blocked.
   */
  createMultiCourtBatch: async (idToken, contactData, bookings) => {
    try {
      const response = await adminApi.post(`/admin/bookings/batch-multi?idToken=${idToken}`, { contactData, bookings });
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) throw new Error(error.response.data.error);
      throw new Error('Failed to create bookings');
    }
  },

  /**
   * Booking templates (F4) — per-club schedule configs. CRUD.
   */
  getBookingTemplates: async (idToken) => {
    try {
      const response = await adminApi.get(`/admin/booking-templates?idToken=${idToken}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) throw new Error(error.response.data.error);
      throw new Error('Failed to fetch templates');
    }
  },
  createBookingTemplate: async (idToken, data) => {
    try {
      const response = await adminApi.post(`/admin/booking-templates?idToken=${idToken}`, data);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) throw new Error(error.response.data.error);
      throw new Error('Failed to save template');
    }
  },
  updateBookingTemplate: async (idToken, id, data) => {
    try {
      const response = await adminApi.put(`/admin/booking-templates/${id}?idToken=${idToken}`, data);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) throw new Error(error.response.data.error);
      throw new Error('Failed to update template');
    }
  },
  deleteBookingTemplate: async (idToken, id) => {
    try {
      const response = await adminApi.delete(`/admin/booking-templates/${id}?idToken=${idToken}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) throw new Error(error.response.data.error);
      throw new Error('Failed to delete template');
    }
  },

  /**
   * Credit packs (F2) — CRUD
   */
  getCreditPacks: async (idToken, courtTypeId) => {
    try {
      const q = courtTypeId ? `&courtTypeId=${courtTypeId}` : '';
      const response = await adminApi.get(`/admin/credit-packs?idToken=${idToken}${q}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) throw new Error(error.response.data.error);
      throw new Error('Failed to fetch credit packs');
    }
  },
  createCreditPack: async (idToken, data) => {
    try {
      const response = await adminApi.post(`/admin/credit-packs?idToken=${idToken}`, data);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) throw new Error(error.response.data.error);
      throw new Error('Failed to create credit pack');
    }
  },
  updateCreditPack: async (idToken, id, data) => {
    try {
      const response = await adminApi.put(`/admin/credit-packs/${id}?idToken=${idToken}`, data);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) throw new Error(error.response.data.error);
      throw new Error('Failed to update credit pack');
    }
  },
  deleteCreditPack: async (idToken, id) => {
    try {
      const response = await adminApi.delete(`/admin/credit-packs/${id}?idToken=${idToken}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) throw new Error(error.response.data.error);
      throw new Error('Failed to delete credit pack');
    }
  },

  /**
   * Get all temporary closures
   */
  getClosures: async (idToken) => {
    try {
      const response = await adminApi.get(`/admin/closures?idToken=${idToken}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) throw new Error(error.response.data.error);
      throw new Error('Failed to fetch closures');
    }
  },

  /**
   * Preview existing bookings a prospective closure would overlap
   */
  getAffectedBookings: async (idToken, { scope, startDate, endDate }) => {
    try {
      const params = new URLSearchParams({ idToken, scope: scope || 'venue', startDate, endDate });
      const response = await adminApi.get(`/admin/closures/affected?${params.toString()}`);
      return response.data.affectedBookings || [];
    } catch (error) {
      if (error.response?.data?.error) throw new Error(error.response.data.error);
      throw new Error('Failed to preview affected bookings');
    }
  },

  /**
   * Create a closure. Returns { closure, affectedBookings }.
   */
  createClosure: async (idToken, closureData) => {
    try {
      const response = await adminApi.post(`/admin/closures?idToken=${idToken}`, closureData);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) throw new Error(error.response.data.error);
      throw new Error('Failed to create closure');
    }
  },

  /**
   * Update a closure
   */
  updateClosure: async (idToken, id, closureData) => {
    try {
      const response = await adminApi.put(`/admin/closures/${id}?idToken=${idToken}`, closureData);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) throw new Error(error.response.data.error);
      throw new Error('Failed to update closure');
    }
  },

  /**
   * Delete a closure
   */
  deleteClosure: async (idToken, id) => {
    try {
      const response = await adminApi.delete(`/admin/closures/${id}?idToken=${idToken}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) throw new Error(error.response.data.error);
      throw new Error('Failed to delete closure');
    }
  },

  /**
   * Get all courts
   * @param {string} idToken - LINE ID token for authentication
   * @returns {Promise<Array>} Array of courts
   */
  getCourts: async (idToken) => {
    try {
      const response = await adminApi.get(`/admin/courts?idToken=${idToken}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to fetch courts');
    }
  },

  /**
   * Create new court
   * @param {string} idToken - LINE ID token for authentication
   * @param {Object} courtData - Court data
   * @returns {Promise<Object>} Created court
   */
  createCourt: async (idToken, courtData) => {
    try {
      const response = await adminApi.post(`/admin/courts?idToken=${idToken}`, courtData);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to create court');
    }
  },

  /**
   * Update court
   * @param {string} idToken - LINE ID token for authentication
   * @param {string} id - Court ID
   * @param {Object} courtData - Updated court data
   * @returns {Promise<Object>} Updated court
   */
  updateCourt: async (idToken, id, courtData) => {
    try {
      const response = await adminApi.put(`/admin/courts/${id}?idToken=${idToken}`, courtData);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to update court');
    }
  },

  /**
   * Delete court
   * @param {string} idToken - LINE ID token for authentication
   * @param {string} id - Court ID
   * @returns {Promise<Object>} Deletion result
   */
  deleteCourt: async (idToken, id) => {
    try {
      const response = await adminApi.delete(`/admin/courts/${id}?idToken=${idToken}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to delete court');
    }
  },

  /**
   * Get all bookings with optional filters
   * @param {string} idToken - LINE ID token for authentication
   * @param {Object} filters - Optional filters
   * @returns {Promise<Array>} Array of bookings
   */
  getBookings: async (idToken, filters = {}) => {
    try {
      const params = new URLSearchParams({ idToken });
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });
      
      const response = await adminApi.get(`/admin/bookings?${params.toString()}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to fetch bookings');
    }
  },

  /**
   * Get all users with optional filters
   * @param {string} idToken - LINE ID token for authentication
   * @param {Object} filters - Optional filters
   * @returns {Promise<Array>} Array of users
   */
  getUsers: async (idToken, filters = {}) => {
    try {
      const params = new URLSearchParams({ idToken });
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });
      
      const response = await adminApi.get(`/admin/users?${params.toString()}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to fetch users');
    }
  },

  /**
   * Update user points (legacy)
   * @param {string} idToken - LINE ID token for authentication
   * @param {string} userId - User ID
   * @param {number} points - Points to add/subtract/set
   * @param {string} action - Action type ('add', 'subtract', 'set')
   * @returns {Promise<Object>} Update result
   */
  updateUserPoints: async (idToken, userId, points, action = 'add') => {
    try {
      const response = await adminApi.put(`/admin/users/${userId}/points?idToken=${idToken}`, {
        points,
        action
      });
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to update user points');
    }
  },

  /**
   * Update user tokens for a specific court type
   * @param {string} idToken - LINE ID token for authentication
   * @param {string} userId - User ID
   * @param {string} courtTypeId - Court type ID
   * @param {number} tokens - Tokens to add/subtract/set
   * @param {string} action - Action type ('add', 'subtract', 'set')
   * @returns {Promise<Object>} Update result
   */
  updateUserTokens: async (idToken, userId, courtTypeId, tokens, action = 'add') => {
    try {
      const response = await adminApi.put(`/admin/users/${userId}/tokens?idToken=${idToken}`, {
        courtTypeId,
        tokens,
        action
      });
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to update user tokens');
    }
  },

  /**
   * Toggle user admin status
   * @param {string} idToken - LINE ID token for authentication
   * @param {string} userId - User ID
   * @param {boolean} isAdmin - Admin status
   * @returns {Promise<Object>} Update result
   */
  toggleUserAdmin: async (idToken, userId, isAdmin) => {
    try {
      const response = await adminApi.put(`/admin/users/${userId}/admin?idToken=${idToken}`, {
        isAdmin
      });
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to update user admin status');
    }
  },

  /**
   * Toggle user staff status
   * @param {string} idToken - LINE ID token for authentication
   * @param {string} userId - User ID
   * @param {boolean} isStaff - Staff status
   * @returns {Promise<Object>} Update result
   */
  toggleUserStaff: async (idToken, userId, isStaff) => {
    try {
      const response = await adminApi.put(`/admin/users/${userId}/staff?idToken=${idToken}`, {
        isStaff
      });
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to update user staff status');
    }
  },


  /**
   * Create multiple admin bookings in batch
   * @param {string} idToken - LINE ID token for authentication
   * @param {Object} batchData - Batch booking data
   * @param {string} batchData.courtId - Court ID
   * @param {Object} batchData.contactData - Contact information
   * @param {string} batchData.contactData.nickname - Customer name/nickname
   * @param {string} batchData.contactData.mobilePhone - Customer phone number
   * @param {Array} batchData.bookings - Array of booking objects
   * @param {string} batchData.bookings[].date - Date in YYYY-MM-DD format
   * @param {string} batchData.bookings[].startTime - Start time in HH:MM format
   * @param {string} batchData.bookings[].endTime - End time in HH:MM format
   * @param {number} batchData.bookings[].duration - Duration in minutes
   * @param {boolean} batchData.isAdminBooking - Whether this is an admin booking
   * @returns {Promise<Object>} Batch creation result
   */
  createAdminBookingBatch: async (idToken, batchData) => {
    try {
      const response = await adminApi.post(`/admin/bookings/batch?idToken=${idToken}`, batchData);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to create admin booking batch');
    }
  },

  /**
   * Cancel booking by admin
   * @param {string} idToken - LINE ID token for authentication
   * @param {string} bookingId - Booking ID to cancel
   * @param {Object} cancelData - Cancel booking data
   * @param {string} cancelData.reason - Reason for cancellation
   * @param {boolean} cancelData.refundTokens - Whether to refund tokens (default: true)
   * @returns {Promise<Object>} Cancel result
   */
  cancelBooking: async (idToken, bookingId, cancelData = {}) => {
    try {
      const response = await adminApi.put(`/admin/bookings/${bookingId}/cancel?idToken=${idToken}`, cancelData);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to cancel booking');
    }
  },

  /**
   * Mark a booking's late/duplicate QR payment as refunded by hand
   * @param {string} note - how it was refunded (e.g. bank transfer + date)
   */
  markRefundDone: async (idToken, bookingId, note = '') => {
    try {
      const response = await adminApi.put(`/admin/bookings/${bookingId}/refund-done?idToken=${idToken}`, { note });
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to mark refund done');
    }
  },

  // ===== Business dashboard (view-only) =====
  getStats: async (idToken, { from, to } = {}) => {
    try {
      const params = new URLSearchParams({ idToken });
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const response = await adminApi.get(`/admin/stats?${params.toString()}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) throw new Error(error.response.data.error);
      throw new Error('Failed to fetch stats');
    }
  },

  getPayments: async (idToken, { from, to, limit, cursor } = {}) => {
    try {
      const params = new URLSearchParams({ idToken });
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (limit) params.set('limit', String(limit));
      if (cursor) params.set('cursor', cursor);
      const response = await adminApi.get(`/admin/payments?${params.toString()}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) throw new Error(error.response.data.error);
      throw new Error('Failed to fetch payments');
    }
  },
};

export default adminApiService;